import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkProcessingPanel = ({ onApplySettings, isProcessing }) => {
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedCriteria, setSelectedCriteria] = useState([]);
  const [priority, setPriority] = useState('normal');
  const [autoAddToPipeline, setAutoAddToPipeline] = useState(false);
  const [minimumScore, setMinimumScore] = useState(70);

  const jobPositions = [
    { value: 'software-engineer', label: 'Software Engineer' },
    { value: 'frontend-developer', label: 'Frontend Developer' },
    { value: 'backend-developer', label: 'Backend Developer' },
    { value: 'fullstack-developer', label: 'Full Stack Developer' },
    { value: 'data-scientist', label: 'Data Scientist' },
    { value: 'product-manager', label: 'Product Manager' },
    { value: 'ui-ux-designer', label: 'UI/UX Designer' },
    { value: 'devops-engineer', label: 'DevOps Engineer' },
    { value: 'qa-engineer', label: 'QA Engineer' },
    { value: 'business-analyst', label: 'Business Analyst' }
  ];

  const screeningCriteria = [
    { id: 'experience', label: 'Years of Experience', description: 'Minimum experience requirements' },
    { id: 'education', label: 'Education Level', description: 'Degree and certification requirements' },
    { id: 'skills', label: 'Technical Skills', description: 'Required programming languages and tools' },
    { id: 'certifications', label: 'Certifications', description: 'Professional certifications' },
    { id: 'location', label: 'Location Preference', description: 'Geographic location requirements' },
    { id: 'salary', label: 'Salary Expectations', description: 'Compensation range matching' },
    { id: 'availability', label: 'Availability', description: 'Start date and notice period' },
    { id: 'language', label: 'Language Skills', description: 'Required language proficiencies' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const handleCriteriaChange = (criteriaId, checked) => {
    if (checked) {
      setSelectedCriteria([...selectedCriteria, criteriaId]);
    } else {
      setSelectedCriteria(selectedCriteria?.filter(id => id !== criteriaId));
    }
  };

  const handleApplySettings = () => {
    const settings = {
      jobPosition: selectedPosition,
      screeningCriteria: selectedCriteria,
      priority,
      autoAddToPipeline,
      minimumScore
    };
    onApplySettings(settings);
  };

  const handleReset = () => {
    setSelectedPosition('');
    setSelectedCriteria([]);
    setPriority('normal');
    setAutoAddToPipeline(false);
    setMinimumScore(70);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Bulk Processing</h3>
            <p className="text-sm text-muted-foreground">Configure AI screening settings</p>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        {/* Job Position Selection */}
        <div>
          <Select
            label="Target Job Position"
            description="Select the position to match candidates against"
            options={jobPositions}
            value={selectedPosition}
            onChange={setSelectedPosition}
            placeholder="Choose a position..."
            searchable
            className="mb-4"
          />
        </div>

        {/* Screening Criteria */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Screening Criteria
          </label>
          <p className="text-sm text-muted-foreground mb-4">
            Select criteria for AI evaluation
          </p>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {screeningCriteria?.map((criteria) => (
              <div key={criteria?.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox
                  checked={selectedCriteria?.includes(criteria?.id)}
                  onChange={(e) => handleCriteriaChange(criteria?.id, e?.target?.checked)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground cursor-pointer">
                    {criteria?.label}
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {criteria?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Processing Priority */}
        <div>
          <Select
            label="Processing Priority"
            description="Set processing queue priority"
            options={priorityOptions}
            value={priority}
            onChange={setPriority}
            className="mb-4"
          />
        </div>

        {/* Minimum Score Threshold */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Minimum AI Score Threshold
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Only candidates above this score will be flagged for review
          </p>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="100"
              value={minimumScore}
              onChange={(e) => setMinimumScore(parseInt(e?.target?.value))}
              className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <div className="w-16 text-center">
              <span className="text-lg font-semibold text-foreground">{minimumScore}</span>
              <span className="text-sm text-muted-foreground">/100</span>
            </div>
          </div>
        </div>

        {/* Auto-add to Pipeline */}
        <div className="border-t border-border pt-4">
          <Checkbox
            label="Auto-add qualified candidates to pipeline"
            description="Automatically add candidates with scores above threshold to recruitment pipeline"
            checked={autoAddToPipeline}
            onChange={(e) => setAutoAddToPipeline(e?.target?.checked)}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={isProcessing}
          >
            Reset Settings
          </Button>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="default"
              onClick={handleApplySettings}
              disabled={isProcessing || !selectedPosition}
              loading={isProcessing}
              iconName="Play"
              iconPosition="left"
            >
              Apply Settings
            </Button>
          </div>
        </div>

        {/* Processing Stats */}
        <div className="bg-muted/30 rounded-lg p-4 mt-4">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="BarChart3" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">Processing Statistics</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Queue Position:</span>
              <span className="ml-2 font-medium text-foreground">#3</span>
            </div>
            <div>
              <span className="text-muted-foreground">Est. Time:</span>
              <span className="ml-2 font-medium text-foreground">~5 min</span>
            </div>
            <div>
              <span className="text-muted-foreground">Success Rate:</span>
              <span className="ml-2 font-medium text-success">94.2%</span>
            </div>
            <div>
              <span className="text-muted-foreground">Avg. Score:</span>
              <span className="ml-2 font-medium text-foreground">78.5</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkProcessingPanel;